import { useMemo } from "react";

import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import { useCatalogTypeVar } from "@shared/reactiveVar/useCatalogType/useCatalogType.store";
import { SpotlightConditions } from "@shared/types/ConditionCampaignType";

import { useCatalogParams } from "../useCatalogParams/useCatalogParams";

/**
 * Custom hook to determine if the Spotlight Campaign feature is enabled.
 *
 * This hook evaluates multiple conditions to check if the Spotlight Campaign
 * feature should be enabled for the current catalog type and parameters.
 *
 * @returns {Object} An object containing:
 * - `isSpotlightCampaignEnabled` (boolean): Indicates whether the Spotlight Campaign is enabled.
 * - `selectedCategory` (Object | null): The selected category from the optimized taxonomies
 *   that matches the predefined conditions, or `null` if no match is found.
 *
 * Conditions for enabling the Spotlight Campaign:
 * - The feature flag `dora_condition_specific_spotlight_campaign` must be enabled.
 * - The catalog type must be "DTP".
 * - A matching category must exist in the `optimizedTaxonomies` parameter.
 */

const useIsSpotlightCampaignEnabled = () => {
  const { type } = useCatalogTypeVar();
  const [catalogParams] = useCatalogParams();
  const location = useLocation();
  const isDTPCatalog = location.pathname.includes("/care-center/");

  const [isSpotlightCampaignFlipperEnabled] = useFlippers(
    "dora_condition_specific_spotlight_campaign"
  );

  const optimizedTaxonomies = catalogParams?.optimizedTaxonomies;
  const selectedCategory = useMemo(
    () =>
      (optimizedTaxonomies ?? []).find(({ name }) => SpotlightConditions.includes(name)) || null,
    [optimizedTaxonomies]
  );

  const isSpotlightCampaignEnabled = !!(
    isSpotlightCampaignFlipperEnabled &&
    (type === "DTP" || isDTPCatalog) &&
    selectedCategory
  );

  return {
    isSpotlightCampaignEnabled,
    selectedCategory,
  };
};

export { useIsSpotlightCampaignEnabled };
